import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import React from "react";
import { Column } from "@tanstack/react-table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { SortDirectionEnum } from "@/__generated__/types";

export interface Sort {
  orderingField: string;
  orderingDirection: SortDirectionEnum;
}

export type OnSortingChange = (sorting: Sort) => void;

interface THeaderCellSortingProps<T> {
  children: React.ReactNode;
  column: Column<T>;
  onSortingChange?: OnSortingChange;
}

export function THeaderCellSorting<T>({
  children,
  column,
  onSortingChange,
}: THeaderCellSortingProps<T>) {
  const { t } = useTranslation();
  const sortDirection = column.getIsSorted();
  const columnName = String(children);

  // ARIA labels for different sort states
  const sortLabel =
    sortDirection === false
      ? "Not sorted"
      : `Sorted ${(sortDirection as SortDirectionEnum) === SortDirectionEnum.Asc ? "ascending" : "descending"}`;

  const handleSortingChange = (direction: SortDirectionEnum) => {
    if (onSortingChange) {
      column.toggleSorting(direction === SortDirectionEnum.Desc);
      onSortingChange({
        orderingField: column.id,
        orderingDirection:
          direction === SortDirectionEnum.Asc
            ? SortDirectionEnum.Asc
            : SortDirectionEnum.Desc,
      });
    } else {
      column.toggleSorting(direction === SortDirectionEnum.Asc);
    }
  };

  return (
    <div
      className="flex items-center p-2 text-primary-light"
      role="region"
      aria-label={t("dataTable.sortingControls")}
    >
      <Popover>
        <PopoverTrigger
          className="flex items-center gap-2 p-2 hover:bg-primary-foreground/50"
          aria-label={t("dataTable.sortColumn", {
            column: columnName,
            sortLabel: sortLabel,
          })}
          role="button"
        >
          {children}
          <span className="ml-2" aria-hidden="true">
            {sortDirection === false && <ChevronsUpDown className="h-4 w-4" />}
            {sortDirection === "asc" && <ArrowUp className="h-4 w-4" />}
            {sortDirection === "desc" && <ArrowDown className="h-4 w-4" />}
          </span>
        </PopoverTrigger>

        <PopoverContent
          className="w-[200px] p-0"
          role="menu"
          aria-label={t("dataTable.sortOptions", {
            column: columnName,
          })}
        >
          <div className="flex flex-col">
            <button
              className="flex items-center gap-2 p-2 hover:bg-primary-foreground"
              onClick={() => handleSortingChange(SortDirectionEnum.Asc)}
              role="menuitem"
              aria-label={t("dataTable.sortAscending", {
                column: columnName,
              })}
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
              <span>{t("global.ascending")}</span>
            </button>
            <button
              className="flex items-center gap-2 p-2 hover:bg-primary-foreground"
              onClick={() => handleSortingChange(SortDirectionEnum.Desc)}
              role="menuitem"
              aria-label={t("dataTable.sortDescending", {
                column: columnName,
              })}
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
              <span>{t("global.descending")}</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
